import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';

const noDefaultValue = () => null;
const available = () => true;

/**
 * IRule for numeric values.
 * It has two specific fields, unitString and factor, and a builde-syntax method withFactor().
 */
export class NumberRule<Context = ScenarioExt> implements IRule<Context> {

  public key: string;
  public unitString: string | undefined; // code of the value unit
  public factor: number = 1; // Value multiplier for display purposes (ex: percents displayed * 100)
  public required: boolean = true;
  public displayed: boolean = true;
  public isInteger: boolean = false;
  public fieldUsage: FieldUsage;
  public getDefaultValue: (context?: Context) => DefaultValue<number> | null;
  public isAvailable: (context: Context) => boolean;

  public constructor(
    key: string,
    unitString?: string,
    isAvailable?: (context: Context) => boolean,
    factor?: number,
    getDefaultValue?: (context?: Context) => DefaultValue<number> | null,
    required?: boolean,
    isInteger?: boolean
  ) {
    this.key = key;
    this.unitString = unitString;
    if (factor) {
      this.factor = factor;
    }
    this.isAvailable = isAvailable ?? available;
    this.getDefaultValue = getDefaultValue ?? noDefaultValue;
    if (required === false || required === true) {
      this.required = required;
    }
    if (isInteger === false || isInteger === true) {
      this.isInteger = isInteger;
    }
    this.fieldUsage = new FieldUsage();
  }

  public get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public checkValue(val: number | null, context: Context): boolean | null {
    if (val === null) {
      return null;
    }
    if (this.isInteger) {
      return Number.isInteger(val);
    }
    return true;
  }

  public withIsInteger(isInteger?: boolean): this { // true is default setting for this function
    this.isInteger = isInteger !== false;
    return this;
  }

  public withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withDefaultValue(getDefaultValue: (context?: Context) => DefaultValue<number> | null): this {
    this.getDefaultValue = getDefaultValue;
    return this;
  }

  public withFactor(factor: number): this {
    this.factor = factor;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }
}
